import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { 
  BookOpen, ChevronLeft, ChevronRight, X, Download, ShoppingCart, 
  ExternalLink, Mail, Check, Lock, Eye, Loader2, Plus, Trash2, Edit2,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_EMAIL = 'massimo_marchiori@yahoo.it';
const CONTACT_EMAIL = 'massimo_marchiori@yahoo.it';

// Descrizione della rivista
const MAGAZINE_DESCRIPTION = `Una Missione di Orientamento: la rivista nasce per essere un punto di riferimento e una voce chiara in difesa del mare, con il motto: "Orientarsi, per non perdersi".

• Sinergia tra Scienza e Esperienza: La testata unisce l'esperienza diretta sul campo (come il recupero delle plastiche) alla divulgazione scientifica rigorosa e alle nuove frontiere della tutela ambientale.

• Un Mosaico di Visioni: Attraverso rubriche dedicate, la rivista esplora il Mediterraneo come cuore pulsante di biodiversità, ma anche come ponte tra culture e laboratorio per la "Blue Economy".

• Un Invito alla Custodia: Respiro Blu non si limita alla denuncia, ma racconta storie di chi vive, studia e protegge il mare, invitando ogni lettore a trasformarsi da semplice spettatore a "custode" attivo del capitale naturale marino.

• Un Progetto Educativo: La rivista è parte di una visione più ampia che include il protocollo MarEdu, portando la voce dell'oceano nelle scuole per formare i futuri decisori del pianeta.

In sintesi, Respiro Blu è uno strumento reale di cambiamento che invita a fermarsi, riprendere fiato e ascoltare ciò che il mare ha da dirci.`;

const MagazineSection = () => {
  const { token, isAdmin } = useAuth();
  const { language } = useLanguage();
  const [magazines, setMagazines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMagazine, setSelectedMagazine] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [editingMagazine, setEditingMagazine] = useState(null);
  const [showInfo, setShowInfo] = useState(true);
  const [showPayPalPopup, setShowPayPalPopup] = useState(false);
  const [paypalPurpose, setPaypalPurpose] = useState(null); // 'digital' o 'subscription'

  // Form admin
  const [adminForm, setAdminForm] = useState({
    title: '',
    issue_number: 1,
    description: '',
    cover_image_url: '',
    preview_pdf_url: '',   // PDF con filigrana per anteprima gratuita
    pdf_download_url: '',  // PDF completo per chi paga
    pages: '',
    price_digital: 5,
    price_print: 19
  });

  // Fetch magazines
  const fetchMagazines = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/magazines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMagazines(response.data);
    } catch (error) {
      console.error('Error fetching magazines:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMagazines();
  }, [fetchMagazines]);

  // Apri viewer
  const openViewer = async (magazine) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/magazines/${magazine.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedMagazine(response.data);
      setCurrentPage(0);
      setViewerOpen(true);
    } catch (error) {
      toast.error('Errore caricamento rivista');
    }
  };

  // Genera link PayPal per acquisto
  const getPayPalLink = (magazine) => {
    const params = new URLSearchParams({
      business: PAYPAL_EMAIL,
      amount: magazine.price_digital.toString(),
      currency_code: 'EUR',
      item_name: `Rivista Respiro Blu - ${magazine.title}`,
      item_number: magazine.id,
      no_shipping: '1',
      return: window.location.href,
      cancel_return: window.location.href
    });
    return `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&${params.toString()}`;
  };

  // Acquista rivista
  const handlePurchase = async (magazine) => {
    // Apri PayPal
    window.open(getPayPalLink(magazine), '_blank');
    
    // Mostra dialog per confermare pagamento
    const confirmed = window.confirm(
      'Hai completato il pagamento su PayPal?\n\nClicca OK dopo aver pagato per sbloccare il download.'
    );
    
    if (confirmed) {
      setPurchasing(true);
      try {
        await axios.post(
          `${BACKEND_URL}/api/magazines/${magazine.id}/purchase`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Acquisto registrato! Ora puoi scaricare la rivista.');
        fetchMagazines();
        if (selectedMagazine?.id === magazine.id) {
          setSelectedMagazine(prev => ({ ...prev, is_purchased: true }));
        }
      } catch (error) {
        toast.error('Errore registrazione acquisto');
      } finally {
        setPurchasing(false);
      }
    }
  };

  // Download PDF
  const handleDownload = async (magazine) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/magazines/${magazine.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Devi acquistare la rivista per scaricarla');
      } else {
        toast.error('Errore download');
      }
    }
  };

  // Admin: Salva rivista
  const handleSaveMagazine = async () => {
    if (!adminForm.title || !adminForm.cover_image_url) {
      toast.error('Compila titolo e copertina');
      return;
    }
    
    if (!adminForm.preview_pdf_url && !adminForm.pdf_download_url) {
      toast.error('Inserisci almeno un link PDF (anteprima o completo)');
      return;
    }

    const data = {
      title: adminForm.title,
      issue_number: adminForm.issue_number,
      description: adminForm.description,
      cover_image_url: adminForm.cover_image_url,
      preview_pdf_url: adminForm.preview_pdf_url,    // PDF con filigrana
      pdf_download_url: adminForm.pdf_download_url,  // PDF completo
      price_digital: adminForm.price_digital,
      price_print: adminForm.price_print,
      pages: []
    };

    try {
      if (editingMagazine) {
        await axios.put(
          `${BACKEND_URL}/api/admin/magazines/${editingMagazine.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rivista aggiornata');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/magazines`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rivista creata');
      }
      setShowAdminForm(false);
      setEditingMagazine(null);
      setAdminForm({
        title: '', issue_number: 1, description: '', cover_image_url: '',
        preview_pdf_url: '', pdf_download_url: '', pages: '', price_digital: 5, price_print: 19
      });
      fetchMagazines();
    } catch (error) {
      toast.error('Errore salvataggio');
    }
  };

  // Admin: Elimina rivista
  const handleDeleteMagazine = async (magazineId) => {
    if (!window.confirm('Eliminare questa rivista?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/magazines/${magazineId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rivista eliminata');
      fetchMagazines();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Admin: Modifica rivista
  const handleEditMagazine = (magazine) => {
    setAdminForm({
      title: magazine.title,
      issue_number: magazine.issue_number,
      description: magazine.description || '',
      cover_image_url: magazine.cover_image_url,
      preview_pdf_url: '',   // L'admin deve reinserirlo se vuole cambiarlo
      pdf_download_url: '',
      pages: '',
      price_digital: magazine.price_digital,
      price_print: magazine.price_print
    });
    setEditingMagazine(magazine);
    setShowAdminForm(true);
  };

  // Apri anteprima gratuita (prima rivista disponibile)
  const handleQuickPreview = () => {
    if (magazines.length > 0) {
      const firstMag = magazines[0];
      if (firstMag.preview_pdf_url) {
        window.open(firstMag.preview_pdf_url, '_blank');
      } else {
        openViewer(firstMag);
      }
    } else {
      toast.info('Nessuna rivista disponibile');
    }
  };

  // Apri popup PayPal per acquisto digitale
  const handleQuickDigitalPurchase = () => {
    if (magazines.length > 0) {
      setSelectedMagazine(magazines[0]);
      setPaypalPurpose('digital');
      setShowPayPalPopup(true);
    } else {
      toast.info('Nessuna rivista disponibile');
    }
  };

  // Apri popup PayPal per abbonamento cartaceo
  const handleSubscriptionPurchase = () => {
    setPaypalPurpose('subscription');
    setShowPayPalPopup(true);
  };

  // Link PayPal generico
  const getGenericPayPalLink = (amount, description) => {
    const params = new URLSearchParams({
      business: PAYPAL_EMAIL,
      amount: amount.toString(),
      currency_code: 'EUR',
      item_name: description,
      no_shipping: '1',
      return: window.location.href,
      cancel_return: window.location.href
    });
    return `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&${params.toString()}`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Barlow Condensed'}}>
          RIVISTA RESPIRO BLU
        </h2>
        <p className="text-cyan-400 text-sm italic">
          "Orientarsi, per non perdersi"
        </p>
      </div>

      {/* Info Box - Collapsible */}
      <div className="bg-slate-900/60 border border-blue-500/30 rounded-2xl mb-6 overflow-hidden">
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <span className="text-white font-semibold">La Rivista</span>
          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showInfo ? 'rotate-90' : ''}`} />
        </button>
        
        {showInfo && (
          <div className="px-4 pb-4">
            <div className="text-slate-300 text-sm whitespace-pre-line leading-relaxed">
              {MAGAZINE_DESCRIPTION}
            </div>
            
            {/* Prezzi - Pulsanti Interattivi */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={handleQuickPreview}
                className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 text-center hover:bg-emerald-500/20 transition-all cursor-pointer"
                data-testid="magazine-preview-btn"
              >
                <Eye className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
                <p className="text-emerald-400 font-bold">GRATIS</p>
                <p className="text-slate-400 text-xs">Sfoglia online</p>
              </button>
              <button
                onClick={handleQuickDigitalPurchase}
                className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 text-center hover:bg-cyan-500/20 transition-all cursor-pointer"
                data-testid="magazine-buy-digital-btn"
              >
                <Download className="w-6 h-6 text-cyan-400 mx-auto mb-1" />
                <p className="text-cyan-400 font-bold">5€</p>
                <p className="text-slate-400 text-xs">Download PDF</p>
              </button>
              <button
                onClick={handleSubscriptionPurchase}
                className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center hover:bg-amber-500/20 transition-all cursor-pointer"
                data-testid="magazine-subscription-btn"
              >
                <Package className="w-6 h-6 text-amber-400 mx-auto mb-1" />
                <p className="text-amber-400 font-bold">19€ / 90€ anno</p>
                <p className="text-slate-400 text-xs">Cartaceo A3</p>
              </button>
            </div>
            
            {/* Contatto per cartaceo */}
            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
              <p className="text-slate-400 text-sm">
                <Mail className="w-4 h-4 inline mr-2" />
                Per ordini cartacei o abbonamento annuale: 
                <a href={`mailto:${CONTACT_EMAIL}?subject=Ordine Rivista Respiro Blu`} className="text-cyan-400 ml-1 hover:underline">
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Admin Button */}
      {isAdmin && (
        <button
          onClick={() => {
            setEditingMagazine(null);
            setAdminForm({
              title: '', issue_number: magazines.length + 1, description: '', 
              cover_image_url: '', preview_pdf_url: '', pdf_download_url: '', pages: '',
              price_digital: 5, price_print: 19
            });
            setShowAdminForm(true);
          }}
          className="w-full mb-4 py-3 bg-amber-500/20 border border-amber-500/50 text-amber-400 rounded-xl flex items-center justify-center gap-2 hover:bg-amber-500/30"
        >
          <Plus className="w-5 h-5" />
          Aggiungi Numero Rivista
        </button>
      )}

      {/* Lista Riviste */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
        </div>
      ) : magazines.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/60 border border-slate-700 rounded-2xl">
          <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nessuna rivista disponibile</p>
          <p className="text-slate-500 text-sm">Il primo numero sarà pubblicato presto!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {magazines.map((magazine) => (
            <div
              key={magazine.id}
              className="bg-slate-900/60 border border-slate-700 rounded-xl overflow-hidden hover:border-cyan-500/50 transition-all"
            >
              {/* Cover */}
              <div className="relative aspect-[3/4] bg-slate-800">
                {magazine.cover_image_url ? (
                  <img
                    src={magazine.cover_image_url}
                    alt={magazine.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-slate-600" />
                  </div>
                )}
                
                {/* Badge stato */}
                <div className="absolute top-2 right-2">
                  {magazine.is_purchased ? (
                    <span className="px-2 py-1 bg-emerald-500 text-white text-xs rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Acquistato
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-cyan-500 text-white text-xs rounded-full">
                      {magazine.price_digital}€
                    </span>
                  )}
                </div>
                
                {/* Admin actions */}
                {isAdmin && (
                  <div className="absolute top-2 left-2 flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEditMagazine(magazine); }}
                      className="p-1.5 bg-slate-900/80 rounded-full text-slate-300 hover:text-white"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteMagazine(magazine.id); }}
                      className="p-1.5 bg-slate-900/80 rounded-full text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="p-3">
                <h3 className="text-white font-semibold text-sm truncate">{magazine.title}</h3>
                <p className="text-slate-500 text-xs">N. {magazine.issue_number}</p>
                
                {/* Actions */}
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => openViewer(magazine)}
                    className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded-lg flex items-center justify-center gap-1"
                  >
                    <Eye className="w-3 h-3" />
                    Sfoglia
                  </button>
                  
                  {magazine.is_purchased ? (
                    <button
                      onClick={() => handleDownload(magazine)}
                      className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-lg flex items-center justify-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Scarica
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePurchase(magazine)}
                      disabled={purchasing}
                      className="flex-1 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-xs rounded-lg flex items-center justify-center gap-1"
                    >
                      <ShoppingCart className="w-3 h-3" />
                      {magazine.price_digital}€
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Viewer Modal - Messaggio prima di aprire il PDF */}
      {viewerOpen && selectedMagazine && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-sm w-full">
            {/* Cover preview */}
            <div className="w-32 h-44 mx-auto mb-4 rounded-lg overflow-hidden shadow-lg">
              <img 
                src={selectedMagazine.cover_image_url} 
                alt={selectedMagazine.title}
                className="w-full h-full object-cover"
              />
            </div>
            
            <h3 className="text-white font-bold text-center mb-2">
              {selectedMagazine.title}
            </h3>
            
            <p className="text-slate-400 text-sm text-center mb-4">
              Stai per visualizzare l'anteprima gratuita della rivista.
            </p>
            
            {/* Info box */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-4">
              <p className="text-cyan-400 text-xs text-center">
                📖 Per ricevere la versione completa senza filigrana, dona <strong>5€</strong>
              </p>
            </div>
            
            {/* Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => {
                  if (selectedMagazine.preview_pdf_url) {
                    window.open(selectedMagazine.preview_pdf_url, '_blank');
                  } else {
                    toast.error('Anteprima non disponibile');
                  }
                }}
                className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
              >
                <Eye className="w-5 h-5" />
                Apri Anteprima (con filigrana)
              </button>
              
              <button
                onClick={() => handlePurchase(selectedMagazine)}
                className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Dona 5€ e ricevi il PDF
              </button>
              
              <button
                onClick={() => setViewerOpen(false)}
                className="w-full py-2 text-slate-500 hover:text-white text-sm"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Form Modal */}
      {showAdminForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-lg my-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                {editingMagazine ? 'Modifica Rivista' : 'Nuova Rivista'}
              </h3>
              <button onClick={() => setShowAdminForm(false)} className="text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Titolo *</label>
                <input
                  type="text"
                  value={adminForm.title}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Respiro Blu N.1 - Gen/Feb 2026"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Numero</label>
                  <input
                    type="number"
                    value={adminForm.issue_number}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, issue_number: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Prezzo digitale €</label>
                  <input
                    type="number"
                    value={adminForm.price_digital}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, price_digital: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">URL Copertina *</label>
                <input
                  type="url"
                  value={adminForm.cover_image_url}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, cover_image_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-emerald-400 mb-1">
                  <Eye className="w-4 h-4 inline mr-1" />
                  PDF Anteprima (con filigrana) - GRATIS
                </label>
                <input
                  type="url"
                  value={adminForm.preview_pdf_url}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, preview_pdf_url: e.target.value }))}
                  placeholder="https://tuosito.com/rivista-anteprima.pdf"
                  className="w-full px-3 py-2 bg-slate-800 border border-emerald-500/30 rounded-lg text-white"
                />
                <p className="text-xs text-slate-500 mt-1">PDF con scritta "ANTEPRIMA" visibile a tutti gratis</p>
              </div>
              
              <div>
                <label className="block text-sm text-cyan-400 mb-1">
                  <Download className="w-4 h-4 inline mr-1" />
                  PDF Completo (senza filigrana) - 5€
                </label>
                <input
                  type="url"
                  value={adminForm.pdf_download_url}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, pdf_download_url: e.target.value }))}
                  placeholder="https://tuosito.com/rivista-completa.pdf"
                  className="w-full px-3 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white"
                />
                <p className="text-xs text-slate-500 mt-1">PDF pulito che riceve chi paga</p>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Descrizione</label>
                <textarea
                  value={adminForm.description}
                  onChange={(e) => setAdminForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="In questo numero..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
                />
              </div>
            </div>
            
            <button
              onClick={handleSaveMagazine}
              className="w-full mt-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              {editingMagazine ? 'Salva Modifiche' : 'Pubblica Rivista'}
            </button>
          </div>
        </div>
      )}

      {/* PayPal Popup */}
      {showPayPalPopup && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">
                {paypalPurpose === 'digital' ? 'Acquista Rivista Digitale' : 'Abbonamento Cartaceo'}
              </h3>
              <button 
                onClick={() => setShowPayPalPopup(false)}
                className="text-slate-500 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {paypalPurpose === 'digital' ? (
              <>
                {/* Cover preview per digitale */}
                {selectedMagazine?.cover_image_url && (
                  <div className="w-24 h-32 mx-auto mb-4 rounded-lg overflow-hidden shadow-lg">
                    <img 
                      src={selectedMagazine.cover_image_url} 
                      alt={selectedMagazine?.title || 'Rivista'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <p className="text-slate-300 text-center mb-4">
                  Ricevi il PDF completo di <strong className="text-cyan-400">{selectedMagazine?.title || 'Respiro Blu'}</strong> senza filigrana.
                </p>
                
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-4 text-center">
                  <p className="text-cyan-400 text-2xl font-bold">5€</p>
                  <p className="text-slate-400 text-xs">Download PDF immediato</p>
                </div>
                
                <button
                  onClick={() => {
                    const link = selectedMagazine 
                      ? getPayPalLink(selectedMagazine)
                      : getGenericPayPalLink(5, 'Rivista Respiro Blu - PDF Digitale');
                    window.open(link, '_blank');
                    setShowPayPalPopup(false);
                    toast.info('Dopo il pagamento, torna qui per sbloccare il download');
                  }}
                  className="w-full py-3 bg-[#0070ba] hover:bg-[#005ea6] text-white font-semibold rounded-lg flex items-center justify-center gap-2"
                  data-testid="paypal-digital-btn"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Paga con PayPal
                </button>
              </>
            ) : (
              <>
                {/* Opzioni abbonamento cartaceo */}
                <p className="text-slate-300 text-center mb-4">
                  Ricevi la rivista stampata in formato <strong className="text-amber-400">A3</strong> direttamente a casa.
                </p>
                
                <div className="space-y-3 mb-4">
                  <button
                    onClick={() => {
                      window.open(getGenericPayPalLink(19, 'Respiro Blu - Numero singolo cartaceo A3'), '_blank');
                      setShowPayPalPopup(false);
                    }}
                    className="w-full py-3 bg-amber-500/20 border border-amber-500/50 text-amber-400 font-semibold rounded-lg flex items-center justify-center gap-2 hover:bg-amber-500/30"
                    data-testid="paypal-single-print-btn"
                  >
                    <Package className="w-5 h-5" />
                    Numero singolo - 19€
                  </button>
                  
                  <button
                    onClick={() => {
                      window.open(getGenericPayPalLink(90, 'Respiro Blu - Abbonamento annuale cartaceo A3 (6 numeri)'), '_blank');
                      setShowPayPalPopup(false);
                    }}
                    className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
                    data-testid="paypal-annual-print-btn"
                  >
                    <Package className="w-5 h-5" />
                    Abbonamento annuale - 90€
                    <span className="ml-1 px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full">-21%</span>
                  </button>
                </div>
                
                <p className="text-slate-500 text-xs text-center">
                  <Mail className="w-3 h-3 inline mr-1" />
                  Per richieste speciali: {CONTACT_EMAIL}
                </p>
              </>
            )}
            
            <button
              onClick={() => setShowPayPalPopup(false)}
              className="w-full mt-3 py-2 text-slate-500 hover:text-white text-sm"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MagazineSection;
